import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { PageHeader, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { KINDS, STATUS_FILTERS, matchStatus, StatusBadge, DaysLeft } from "@/components/documents/docConst";
import { DocumentDialog } from "@/components/documents/DocumentDialog";
import { VersionDialog } from "@/components/documents/VersionDialog";
import { DocumentSheet } from "@/components/documents/DocumentSheet";
import { DocumentsCalendar } from "@/components/documents/DocumentsCalendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Kpi = ({ label, value, tone, testId, onClick }) => (
  <button type="button" onClick={onClick} className={`border-l-4 bg-white px-5 py-4 text-left transition-colors hover:bg-muted/40 ${tone}`} data-testid={testId}>
    <div className="micro-label">{label}</div>
    <div className="mt-1 font-display text-3xl font-extrabold">{value}</div>
  </button>
);

const Filter = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full bg-white sm:w-52" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Documents() {
  const { can, user } = useAuth();
  const { visibleCompanies: companies, nameOf, companyId } = useData();
  const [all, setDocs] = useState([]);
  const docs = useMemo(() => (companyId ? all.filter((d) => d.company_id === companyId) : all), [all, companyId]);
  const [owners, setOwners] = useState([]);
  const [f, setF] = useState({ kind: "all", company: companyId || "all", status: "all", q: "" });
  useEffect(() => { setF((s) => ({ ...s, company: companyId || "all" })); }, [companyId]);
  const [editing, setEditing] = useState(null);
  const [renewing, setRenewing] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => { const d = params.get("doc"); if (d) { setOpenId(d); setParams({}, { replace: true }); } }, [params, setParams]);
  const load = useCallback(() => api.get("/documents").then((r) => setDocs(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/documents/owners").then((r) => setOwners(r.data)).catch(() => {}); }, [load]);
  const rows = useMemo(() => docs.filter((d) => (f.kind === "all" || d.kind === f.kind) && (f.company === "all" || (d.company_id || "group") === f.company) && matchStatus(d, f.status)
    && (!f.q || `${d.title} ${d.counterparty} ${d.number}`.toLowerCase().includes(f.q.toLowerCase()))), [docs, f]);
  const open = all.find((d) => d.id === openId);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const saved = () => { setEditing(null); setRenewing(null); load(); };
  const remove = async () => {
    if (!window.confirm(`Eliminare "${open.title}" e tutte le sue versioni?`)) return;
    try { await api.delete(`/documents/${open.id}`); toast.success("Documento eliminato"); setOpenId(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const count = (s) => docs.filter((d) => matchStatus(d, s)).length;
  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow="Contratti · certificazioni · procedure" title="Documentale">
        {can("docs:write") && <Button onClick={() => setEditing("new")} data-testid="document-new-button"><Plus className="mr-1 h-4 w-4" />Nuovo documento</Button>}
      </PageHeader>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Kpi label="Scaduti o in scadenza oggi" value={count("expired")} tone="border-red-600" testId="docs-kpi-expired" onClick={() => set("status")("expired")} />
        <Kpi label="In scadenza entro 60 giorni" value={count("soon")} tone="border-amber-500" testId="docs-kpi-soon" onClick={() => set("status")("soon")} />
        <Kpi label="Documenti in archivio" value={docs.length} tone="border-slate-400" testId="docs-kpi-total" onClick={() => set("status")("all")} />
      </div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative sm:w-72"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="bg-white pl-9" placeholder="Cerca titolo, controparte, numero…" value={f.q} onChange={(e) => set("q")(e.target.value)} data-testid="docs-search" /></div>
        <Filter value={f.kind} onChange={set("kind")} options={[["all", "Tutti i tipi"], ...Object.entries(KINDS)]} testId="docs-filter-kind" />
        <Filter value={f.company} onChange={set("company")} options={[["all", "Tutte le società"], ...(companyId ? [] : [["group", "Documenti di gruppo"]]), ...companies.map((c) => [c.id, c.legal_name])]} testId="docs-filter-company" />
        <Filter value={f.status} onChange={set("status")} options={Object.entries(STATUS_FILTERS)} testId="docs-filter-status" />
      </div>
      <Tabs defaultValue="list">
        <TabsList className="mb-4">
          <TabsTrigger value="list" data-testid="docs-tab-list">Elenco</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="docs-tab-calendar">Calendario</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar"><DocumentsCalendar docs={rows} onOpen={setOpenId} /></TabsContent>
        <TabsContent value="list">
      {rows.length === 0 ? <Empty text={docs.length ? "Nessun documento con questi filtri" : "Archivio vuoto: aggiungi il primo contratto, certificazione o procedura"} testId="docs-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Stato</TableHead><TableHead>Tipo</TableHead><TableHead>Documento</TableHead><TableHead>Società</TableHead><TableHead>Scadenza / revisione</TableHead><TableHead>Responsabile</TableHead><TableHead className="text-right">Versioni</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id} className="cursor-pointer" onClick={() => setOpenId(d.id)} data-testid={`document-row-${d.id}`}>
                  <TableCell><StatusBadge d={d} testId={`document-status-${d.id}`} /></TableCell>
                  <TableCell className="text-xs uppercase tracking-wide text-muted-foreground">{KINDS[d.kind]}</TableCell>
                  <TableCell><div className="font-semibold">{d.title}</div><div className="text-xs text-muted-foreground">{[d.counterparty, d.number].filter(Boolean).join(" · ")}</div></TableCell>
                  <TableCell className="text-xs">{d.company_id ? nameOf("companies", d.company_id, "code") : "Gruppo"}</TableCell>
                  <TableCell><DaysLeft d={d} /></TableCell>
                  <TableCell className="text-sm">{owners.find((u) => u.id === d.owner_user_id)?.name || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{d.versions.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
        </TabsContent>
      </Tabs>
      <p className="mt-3 text-xs text-muted-foreground">Ogni mattina il responsabile del documento e gli amministratori ricevono un avviso a 60, 30, 7 giorni e il giorno della scadenza o revisione.</p>
      <DocumentSheet doc={open} owners={owners} canWrite={can("docs:write") && (!!open?.company_id || user.role === "admin" || !!user.group_scope)} onClose={() => setOpenId(null)} onEdit={() => setEditing(open)} onRenew={() => setRenewing(open)} onDelete={remove} />
      <DocumentDialog doc={editing} owners={owners} onClose={() => setEditing(null)} onSaved={saved} />
      <VersionDialog doc={renewing} onClose={() => setRenewing(null)} onSaved={saved} />
    </div>
  );
}
